<template>
  <div v-if="open" class="detail-page" role="region" aria-label="作品詳細">
    <header class="detail-page__bar">
      <button type="button" class="detail-page__back" @click="$emit('close')">
        <i class="fas fa-arrow-left" aria-hidden="true" />
        <span>戻る</span>
      </button>
      <h2 class="detail-page__title">作品詳細</h2>
    </header>

    <div class="detail-page__body">
      <div class="detail-page__inner">
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  open: {
    type: Boolean,
    default: false,
  },
})
defineEmits(['close'])
</script>

<style lang="less" scoped>
/*
 * 作品詳細は「右から出るドロワー」ではなく独立したページとして扱う。
 * 画面全体を覆い、左上の戻るボタンだけで一覧に戻る導線にする
 * （ドロワーだと背後の一覧が見えていて、どちらを操作しているのか分かりにくかった）。
 *
 * z-index は cinema-weekly-drawer(1900/1910) より低く保つ：
 * 劇場名クリックで開く週次詳細は、このページの上に重なる必要がある。
 */
.detail-page {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  /* サイドバー分は避けて開く（.main-container の margin-left と同じ考え方）。
     折りたたみ時は --sidebar-width 自体が 54px に変わるので自動追従する */
  left: var(--sidebar-width, 340px);
  z-index: 1700;
  display: flex;
  flex-direction: column;
  background: var(--bg-body, #fff);
  transition: left 0.28s;
}

/* サイドバーを完全に隠した場合、およびモバイル（サイドバーは浮上表示でレイアウト幅に含まれない）は
   画面左端まで表示する */
.sidebarHide .detail-page,
.mobile .detail-page {
  left: 0;
}

/* 戻るバーは master(prod) の .mobile-detail-toolbar / .mobile-detail-back を
   そのまま踏襲する。プロダクトで見慣れた戻る導線と見た目を変えないための踏襲であり、
   数値を「良さそうな値」に変えないこと */
.detail-page__bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
  box-sizing: border-box;
  width: 100%;
  padding: 12px 14px;
  /* iPhone のノッチ/ステータスバーに戻るボタンが潜り込まないようにする */
  padding-top: calc(12px + env(safe-area-inset-top, 0px));
  border-bottom: 1px solid var(--mc-border, #e5e5e5);
  background: color-mix(in srgb, var(--brand-color, #f9da49) 14%, var(--mc-bg, #fff));
  z-index: 5;
}

.detail-page__back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-height: 40px;
  padding: 8px 16px;
  border: 1px solid color-mix(in srgb, var(--brand-color, #f9da49) 45%, var(--mc-border, #e5e5e5));
  border-radius: 999px;
  background: var(--brand-color, #f9da49);
  color: #181818;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 3px color-mix(in srgb, #000 12%, transparent);
}

.detail-page__title {
  margin: 0;
  color: var(--mc-text, #000);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.detail-page__body {
  flex: 1;
  min-height: 0;
  padding: 16px;
  /* 縦スクロールはここ1箇所に集約する。横方向はワイドテーブル
     （cinema-table-wrap）側で個別にスクロールさせるため隠す */
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.detail-page__inner {
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* 画面いっぱいに広げず中央に置く。16列の劇場テーブルが収まる下限として 1280px */
  max-width: 1280px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  /* 戻るバーは master(prod) と同じ寸法のままにする（スマホが本来の表示環境のため） */
  .detail-page__body {
    padding: 10px;
  }
}
</style>
